import './VideoScriptGenerator.css';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { canUseDOM, get, getPageThemeColors } from '../../utils';
import { createMapFromDictionary } from '../../utils/createMapFromDictionary';
import { useSitecoreContext } from '../../utils/SitecoreContextReactContext';
import { VideoScriptGeneratorModal } from './components/VideoScriptGeneratorModal';
import { VideoScriptStep } from './components/VideoScriptStep';
import { useCollectedData } from './hooks/useCollectedData';
import { hexToRgb } from '../../utils/hexToRgb';
import { VideoScriptStepsLine } from './components/VideoScriptStepsLine';
import { getLatestUserCookie, getUserCookie } from '../Login/user_session';
import { fetchData } from '../../utils/fetchData';
import { VideoFinalStep } from './components/VideoFinalStep';
import { VideoThankYouMessage } from './components/VideoThankYouMessage';
import { LOCAL_STORAGE_KEY_VIDEO_COLLECTION_DATA, LOCAL_STORAGE_KEY_LINK_TO_VIDEO_SCRIPT_GENERATOR } from './constants';
import { Placeholder } from '@sitecore-jss/sitecore-jss-react';
import { convertToAnalyticObject } from '../../utils/analytics';
import { tealiumClickFunc, tealiumMergeClickData } from '../../utils/tealium';
const getNavBottomPosition = () => {
  const navHolder = canUseDOM ? document.querySelector('.navHolder') : null;
  return navHolder ? navHolder.offsetHeight : 88;
};
const VideoScriptGenerator = _ref => {
  let {
    fields,
    rendering,
    params
  } = _ref;
  const sitecoreContext = useSitecoreContext();
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const identityGlobal = get(sitecoreContext, 'configuration.globalApi.identityGlobal') || 'https://replaceidentityGlobal';
  const apiGlobal = get(sitecoreContext, 'configuration.globalApi.apiGlobal') || 'https://replaceapiGlobal';
  const clientId = get(sitecoreContext, 'configuration.globalApi.clientId') || 'replaceint2';
  const clientSecret = get(sitecoreContext, 'configuration.globalApi.clientSecret') || 'replacesecret';
  const primaryPageThemeColor = get(sitecoreContext, 'themesOfPage.primary');
  const selectedPrimaryColor = primaryPageThemeColor ? primaryPageThemeColor : getPageThemeColors().primary;
  const [navBottom, setNavBottom] = useState(getNavBottomPosition());
  useEffect(() => {
    let timeoutId = null;
    const handleResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        setNavBottom(getNavBottomPosition());
      }, 200);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);
  const variables = {
    '--video-script-generator-color': selectedPrimaryColor,
    '--video-script-generator-border-color-with-opacity': hexToRgb(selectedPrimaryColor).concat(', 0.9'),
    '--video-script-generator-background-color-with-opacity': hexToRgb(selectedPrimaryColor).concat(', 0.1'),
    '--mainNavHeight-variable': `${navBottom}px`
  };
  const dictionary = get(fields, 'data.dictionary.dictionaryValues', []);
  const titleButton = get(fields, 'data.configuration.title.jss.value', '');
  const mapDictionary = createMapFromDictionary(dictionary);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [userProfile, setUserProfile] = React.useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [hasLoadedProfile, setHasLoadedProfile] = useState(false);
  const linkToVideoScriptGenerator = canUseDOM ? localStorage.getItem(LOCAL_STORAGE_KEY_LINK_TO_VIDEO_SCRIPT_GENERATOR) || null : null;
  const scriptSteps = useMemo(() => get(fields, 'data.configuration.scriptSteps', []), [fields]);
  const scriptStep = useMemo(() => scriptSteps[currentStep], [scriptSteps, currentStep]);
  const navigationLeftIcon = useMemo(() => get(scriptStep, 'navigationLeftIcon.targetItem.svg.jss.value', ''), [scriptStep]);
  const navigationRightIcon = useMemo(() => get(scriptStep, 'navigationRightIcon.targetItem.svg.jss.value', ''), [scriptStep]);
  const stepNames = useMemo(() => scriptSteps.map(el => el.title.jss.value), [scriptSteps]);
  const loginStatus = getUserCookie().login_status;
  const [watcherOverLinkToVideoScriptGenerator, setWatcherOverLinkToVideoScriptGenerator] = useState(linkToVideoScriptGenerator);
  const {
    collectedData,
    setScriptStepData,
    resetData
  } = useCollectedData(scriptSteps);
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const openModal = useCallback(() => {
    if (canUseDOM) {
      setIsModalOpened(true);
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }
  }, []);
  const removeFromLocalStorage = () => {
    if (canUseDOM) {
      localStorage.removeItem(LOCAL_STORAGE_KEY_LINK_TO_VIDEO_SCRIPT_GENERATOR);
      setWatcherOverLinkToVideoScriptGenerator("");
      localStorage.removeItem(LOCAL_STORAGE_KEY_VIDEO_COLLECTION_DATA);
    }
  };
  const hideModal = useCallback(() => {
    if (canUseDOM) {
      removeFromLocalStorage();
      setIsModalOpened(false);
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    }
  }, []);
  const goBack = useCallback(() => {
    if (currentStep === 0) {
      hideModal();
      return;
    }
    setCurrentStep(prev => prev - 1);
  }, [currentStep, hideModal]);
  const goNext = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);
  useEffect(() => {
    if (!pageEditing && !hasLoadedProfile && (currentStep === scriptSteps.length || linkToVideoScriptGenerator) && loginStatus) {
      setIsLoadingProfile(true);
      getLatestUserCookie(identityGlobal, clientId, clientSecret).then(userCookie => {
        if (userCookie.login_status && !userCookie.error) {
          fetchData({
            path: `${apiGlobal}/graphql/user`,
            options: {
              method: 'POST',
              body: JSON.stringify({
                query: `{ userProfile { firstName, lastName, email } }`
              })
            },
            authToken: userCookie.access_token
          }).then(data => {
            const {
              firstName,
              lastName,
              email
            } = data.data.userProfile;
            setUserProfile({
              firstName,
              lastName,
              email
            });
            setIsLoadingProfile(false);
            setHasLoadedProfile(true);
          });
        } else {
          setIsLoadingProfile(false);
          setHasLoadedProfile(true);
        }
      });
    }
  }, [currentStep, scriptSteps.length, linkToVideoScriptGenerator, loginStatus, hasLoadedProfile]);
  useEffect(() => {
    if (linkToVideoScriptGenerator && userProfile.firstName) {
      openModal();
    }
  }, [linkToVideoScriptGenerator, openModal, userProfile]);
  return /*#__PURE__*/React.createElement("div", {
    className: commonStyles,
    style: variables
  }, /*#__PURE__*/React.createElement("button", {
    className: "btn btn-primary",
    onClick: () => {
      openModal();
      tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['StartButtonClickAnalytics'], params), titleButton);
    }
  }, titleButton), pageEditing ? /*#__PURE__*/React.createElement(Placeholder, {
    name: "video-script-generator",
    rendering: rendering
  }) : null, (currentStep === scriptSteps.length || linkToVideoScriptGenerator) && isLoadingProfile ? /*#__PURE__*/React.createElement("div", {
    className: "loaderContainer"
  }, /*#__PURE__*/React.createElement("div", {
    className: "loader-t1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "loadingio-spinner-rolling-arlp0sgzkfu"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ldio-fryp34cx4ij"
  }, /*#__PURE__*/React.createElement("div", null))))) : null, isModalOpened && /*#__PURE__*/React.createElement(VideoScriptGeneratorModal, {
    hide: hideModal,
    rtl: rtl
  }, currentStep < scriptSteps.length && (!linkToVideoScriptGenerator || !userProfile.firstName && !isLoadingProfile) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "videoScript__header module-group-script-generator"
  }, /*#__PURE__*/React.createElement(VideoScriptStepsLine, {
    rtl: rtl,
    stepNames: stepNames,
    currentStepId: currentStep
  })), /*#__PURE__*/React.createElement("div", {
    className: "modal-content module-group-script-generator"
  }, /*#__PURE__*/React.createElement(VideoScriptStep, {
    rtl: rtl,
    setScriptStepData: setScriptStepData,
    title: scriptStep.subtitle.jss.value || '',
    scriptSteps: scriptSteps,
    choiceItems: scriptStep.cards.map((el, index) => ({
      id: index + 1,
      cardKey: el.key.jss.value,
      title: el.title.jss.value,
      svgIcon: el.iconCode.jss.value,
      image: el.imageOrIcon.jss.value.src
    })),
    nextLabel: scriptStep.navigationLabel.jss.value || '',
    backLabel: mapDictionary.get('Back') || '',
    onNextClick: goNext,
    onBackClick: goBack,
    currentStep: currentStep,
    collectedData: collectedData,
    navigationLeftIcon: navigationLeftIcon,
    navigationRightIcon: navigationRightIcon,
    params: params,
    analytics: eventsAnalytics,
    loginStatus: loginStatus
  }))), currentStep === scriptSteps.length && !userProfile.firstName && !isLoadingProfile ? /*#__PURE__*/React.createElement(VideoFinalStep, {
    fields: fields,
    analytics: eventsAnalytics,
    params: params,
    collectedData: collectedData,
    rtl: rtl
  }) : null, (currentStep === scriptSteps.length || linkToVideoScriptGenerator || watcherOverLinkToVideoScriptGenerator) && userProfile.firstName ? /*#__PURE__*/React.createElement(VideoThankYouMessage, {
    fields: fields,
    rendering: rendering,
    hideModal: hideModal,
    mapDictionary: mapDictionary,
    removeFromLocalStorage: removeFromLocalStorage,
    setCurrentStep: setCurrentStep,
    userProfile: userProfile,
    sitecoreContext: sitecoreContext,
    collectedData: collectedData,
    resetData: resetData,
    rtl: rtl,
    analytics: eventsAnalytics,
    params: params
  }) : null));
};
VideoScriptGenerator.displayName = "VideoScriptGenerator";
export default /*#__PURE__*/memo(VideoScriptGenerator);
const commonStyles = "cb6n9df";